<template>
  <VDataGridV2
    clickable
    paginated
    show-action-bar
    entity-type="partner"
    :loader="dataLoader"
    @row:dblclick="row => openPartnerCard(row)"
    :context-menu="PartnersService.contextMenu"
  >
    <VDataGridColumnV2
      id="id"
      label="№"
      width="50px"
    >
      <template #default="{ row }">
        <VLink @click="openPartnerCard(row)">{{ row.id }}</VLink>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="partnerPattern"
      label="Партнер"
      width="250px"
      content-class="ellipsis"
      filterable
      filter-send-name="pattern"
      :filter-props="{ placeholder: 'Введите название', clearable: true }"
    >
      <template #default="{ row }">
        {{ row.name }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="types"
      label="Тип"
      width="150px"
      content-class="ellipsis"
      filterable
      filter-type="select"
      :filter-props="{ options: PartnersService.partnerTypeList, clearable: true, multiple: true }"
    >
      <template #default="{ row }">
        {{ row.isSeller ? 'Поставщик' : row.isBuyer ? 'Покупатель' : 'Транспортная компания' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="category"
      label="Категория"
      width="150px"
      content-class="ellipsis"
      filter-send-name="categoryId"
      filterable
      filter-type="select"
      :filter-props="{ options: PartnerCategoriesService.partnerCategories, clearable: true }"
    >
      <template #default="{ row }">
        {{ row.category ? row.category.name : 'Не указан' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="isOperationsAllowed"
      label="Доступ"
      width="90px"
      content-align="center"
      filterable
      filter-type="select"
      :filter-props="{ options: PartnersService.isOperationsAllowedList }"
    >
      <template #default="{ row }">
        {{ row.isOperationsAllowed ? 'Полный' : 'Тестовый' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="companyCountry"
      label="Страна юр. лица"
      width="140px"
      content-align="center"
      filterable
      filter-identity="*"
      filter-type="select"
      :filter-props="{ searchFunction: CountriesService.search, clearable: true }"
    >
      <template #default="{ row }">
        {{ row.country?.name ?? 'Не указана' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="hasCompany"
      label="Юр. лица"
      width="90px"
      content-align="center"
      filterable
      filter-type="select"
      :filter-props="{ options: [{ id: true, name: 'Есть' }, { id: false, name: 'Нет' }], clearable: true }"
    >
      <template #default="{ row }">
        {{ row.stats?.companies?.qty > 0 ? 'Есть' : 'Нет' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="hasContract"
      label="Договор"
      width="80px"
      content-align="center"
      filterable
      filter-type="select"
      :filter-props="{ options: [{ id: true, name: 'Есть' }, { id: false, name: 'Нет' }], clearable: true }"
    >
      <template #default="{ row }">
        {{ row.stats?.contracts?.qty > 0 ? 'Есть' : 'Нет' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="company"
      label="Юр. лица"
      width="150px"
      content-class="ellipsis"
      filterable
      filter-send-name="companyPattern"
      :filter-props="{ placeholder: 'Введите название', clearable: true }"
    >
      <template #default="{ row }">
        {{ row.stats?.companies?.description }} <span v-if="row.stats?.companies?.qty > 1"> и ещё {{ row.stats.companies.qty - 1 }}</span>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="person"
      label="Контактные лица"
      width="150px"
      content-class="ellipsis"
      filterable
      filter-send-name="personPattern"
      :filter-props="{ placeholder: 'Введите email или имя', clearable: true }"
    >
      <template #default="{ row }">
        {{ row.stats.persons.description }} <span v-if="row.stats.persons.qty > 1"> и ещё {{ row.stats.persons.qty - 1 }}</span>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="responsible"
      label="Ответственные"
      width="150px"
      content-class="ellipsis"
      filterable
      filter-identity="*"
      filter-send-name="responsible"
      filter-type="select"
      :filter-props="{ placeholder: 'Введите ФИО', searchFunction: PersonsService.searchPersons, clearable: true }"
    >
      <template #default="{ row }">
        <template v-if="row.responsible.length > 0">
          {{ row.responsible[0].name }}<span v-if="row.responsible.length > 1"> и ещё {{ row.responsible.length - 1 }}</span>
        </template>
        <template v-else>Не назначен</template>
      </template>
    </VDataGridColumnV2>

    <VDataGridActionBarV2>
      <VButton theme="primary" @click="createPartner">
        Создать партнера
      </VButton>
    </VDataGridActionBarV2>
  </VDataGridV2>
</template>

<script lang="ts">

import type Partner               from "@/domains/Partners/Partner";
import DataGridLoader             from '@/lib/components/VDataGridV2/DataGridLoader';
import PartnersService            from '@/domains/Partners/PartnersService';
import PersonsService             from "@/domains/Persons/PersonsService";
import PartnerCategoriesService   from "@/domains/PartnerCategories/PartnerCategoriesService";
import CountriesService           from "@/domains/Countries/CountriesService";

export default defineComponent({
  name: 'PartnersRegistry',

  setup() {
    return {
      PartnersService,
      PersonsService,
      PartnerCategoriesService,
      CountriesService,
    };
  },

  data() {
    return {
      dataLoader: new DataGridLoader(PartnersService.getListPaginated.bind(PartnersService)),
    };
  },

  methods: {
    async openPartnerCard(row: Partner): Promise<void> {
      if (await PartnersService.showPartnerCard(row) === true) {
        await this.dataLoader.refresh();
      }
    },

    /** Созданного партнёра сразу открываем — дозаполнить контакты, юр. лица и счета */
    async createPartner(): Promise<void> {
      const partner = await PartnersService.createPartner();

      if (partner !== null) {
        await this.dataLoader.refresh();
        await this.openPartnerCard(partner);
      }
      else {
        // карточку закрыли без создания
      }
    },
  },
});
</script>